import { createElement, createIcon, KEYBOARD_TRAY_ID } from './keyboard-dom.js';

const NUMERIC_THEME_CLASS = 'app-kb--numeric';
const OPTIONS_KEY = '__appKeyboardOptions';
const SHARED_ENGINE_KEY = Symbol.for('kiosk.keyboardEngine');

export function normalizeInputElementForVirtualKeyboard(el) {
    if (!el) {
        throw new Error('normalizeInputElementForVirtualKeyboard: element is required');
    }
    if (el.tagName !== 'INPUT') {
        return;
    }
    const type = (el.type || '').toLowerCase();
    if (['text', 'search', 'url', 'tel', 'password'].includes(type)) {
        return;
    }
    el.setAttribute('data-vk-original-input-type', type);
    el.type = 'text';
    if (el.hasAttribute('inputmode')) {
        return;
    }
    if (type === 'email') {
        el.setAttribute('inputmode', 'email');
        return;
    }
    if (type === 'number') {
        const step = el.getAttribute('step');
        const allowFraction = !step || step === 'any' || step.includes('.');
        el.setAttribute('inputmode', allowFraction ? 'decimal' : 'numeric');
        return;
    }
    el.setAttribute('inputmode', 'text');
}

function getFieldPreviewLabel(el) {
    const ariaLabel = (el.getAttribute('aria-label') || '').trim();
    if (ariaLabel) {
        return ariaLabel;
    }
    if (el.labels && el.labels.length) {
        const clone = el.labels[0].cloneNode(true);
        clone.querySelectorAll([
            'small',
            'i',
            'svg',
            'title',
            '.sr-only',
            '.visually-hidden',
            '[aria-hidden="true"]',
            '[data-toggle="tooltip"]',
            '[data-bs-toggle="tooltip"]',
            '[class*="fa-"]',
        ].join(', ')).forEach((node) => node.remove());
        const text = clone.textContent.replace(/\s+/g, ' ').trim();
        if (text) {
            return text;
        }
    }
    const placeholder = (el.getAttribute('placeholder') || '').trim();
    if (placeholder) {
        return placeholder;
    }
    return '';
}

function isInteractableElement(el) {
    if (!el || el.disabled || el.hidden) {
        return false;
    }
    if (el.getAttribute('aria-hidden') === 'true') {
        return false;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
    }
    return el.getClientRects().length > 0;
}

function listKeyboardFields(scopeEl) {
    const root = scopeEl.form || scopeEl.closest('form') || document;
    return Array.from(root.querySelectorAll('.app-kb-target')).filter(isInteractableElement);
}

function findNextKeyboardField(el) {
    const fields = listKeyboardFields(el);
    const index = fields.indexOf(el);
    if (index < 0) {
        return null;
    }
    return fields[index + 1] || null;
}

function findNamedAcceptControl(el) {
    const fieldId = String(el.id || '').trim();
    if (!fieldId) {
        return null;
    }
    return document.querySelector('[data-vk-accept-for="' + CSS.escape(fieldId) + '"]');
}

function liftReadOnlyForValidation(el, fn) {
    const wasReadOnly = el.readOnly;
    el.readOnly = false;
    try {
        return fn();
    } finally {
        if (wasReadOnly) {
            el.readOnly = true;
        }
    }
}

function validityPrototype(el) {
    if (el instanceof HTMLTextAreaElement) {
        return HTMLTextAreaElement.prototype;
    }
    return HTMLInputElement.prototype;
}

function patchConstraintMethods(el) {
    if (el.__appKbValidityPatched) {
        return;
    }
    el.__appKbValidityPatched = true;
    const proto = validityPrototype(el);
    el.checkValidity = function checkAppKbValidity() {
        return liftReadOnlyForValidation(el, () => proto.checkValidity.call(el));
    };
    el.reportValidity = function reportAppKbValidity() {
        return liftReadOnlyForValidation(el, () => proto.reportValidity.call(el));
    };
    el.setCustomValidity = function setAppKbCustomValidity(message) {
        liftReadOnlyForValidation(el, () => proto.setCustomValidity.call(el, message));
    };
}

function bindFormConstraintBridge(el) {
    const form = el.form || el.closest('form');
    if (!form || form.__appKbConstraintBound) {
        return;
    }
    form.__appKbConstraintBound = true;
    form.addEventListener('submit', onKeyboardFormSubmit, true);
}

function onKeyboardFormSubmit(event) {
    const form = event.currentTarget;
    const fields = Array.from(form.querySelectorAll('.app-kb-target'));
    for (const field of fields) {
        if (field.checkValidity()) {
            continue;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        field.reportValidity();
        return;
    }
}

function findFormSubmitControl(el) {
    const form = el.form || el.closest('form');
    if (!form) {
        return null;
    }
    const explicit = Array.from(
        form.querySelectorAll('button[type="submit"], input[type="submit"]')
    ).find(isInteractableElement);
    if (explicit) {
        return explicit;
    }
    return Array.from(form.querySelectorAll('button:not([type])')).find(isInteractableElement) || null;
}

function getControlLabel(control) {
    if (control.tagName === 'INPUT') {
        return (control.value || '').trim();
    }
    const clone = control.cloneNode(true);
    clone.querySelectorAll('i, svg, [aria-hidden="true"]').forEach((node) => node.remove());
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
}

class KeyboardEngine {
    constructor() {
        this.state = null;
        this.tray = null;
        this.rowsEl = null;
        this.previewEl = null;
        this.previewLabelEl = null;
        this.previewTextEl = null;
        this.previewBeforeEl = null;
        this.previewAfterEl = null;
        this.errorEl = null;
        this.dismissButton = null;
        this.primaryButton = null;
        this.stopActiveRepeat = null;
        document.addEventListener('selectionchange', () => {
            if (this.state && document.activeElement === this.state.el) {
                this.updatePreview();
            }
        });
        document.addEventListener('invalid', (event) => {
            this.onFieldInvalid(event);
        }, true);
        document.addEventListener('keydown', (event) => {
            this.dismissFromEscape(event);
        }, true);
        document.addEventListener('keydown', (event) => {
            this.typeFromPhysicalKey(event);
        });
    }

    dismissFromEscape(event) {
        if (!this.state || (event.key !== 'Escape' && event.key !== 'Esc')) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.hide();
    }

    typeFromPhysicalKey(event) {
        if (!this.state || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) {
            return;
        }
        const map = this.state.options.physicalKeys;
        if (!map || event.target !== this.state.el) {
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            this.hide();
            return;
        }
        const text = map[event.code];
        if (!text) {
            return;
        }
        event.preventDefault();
        this.insertText(text);
    }

    ensureTray() {
        if (this.tray) {
            return;
        }
        const previewLabel = createElement('span', { className: 'app-kb__preview-label' });
        const previewBefore = createElement('span', { className: 'app-kb__caret-before' });
        const previewAfter = createElement('span', { className: 'app-kb__caret-after' });
        const caretBar = createElement('span', { className: 'app-kb__caret-bar' });
        const caretHandle = createElement('span', { className: 'app-kb__caret-handle' });
        const caretMarker = createElement('span', {
            className: 'app-kb__caret-marker',
            children: [caretBar, caretHandle]
        });
        this.caretMarkerEl = caretMarker;
        const previewText = createElement('span', {
            className: 'app-kb__preview-text',
            children: [previewBefore, caretMarker, previewAfter]
        });
        const dismissButton = createElement('button', {
            className: 'app-kb__action app-kb__action--dismiss',
            attrs: { type: 'button' },
            text: 'Close'
        });
        const primaryButton = createElement('button', {
            className: 'app-kb__action app-kb__action--primary',
            attrs: { type: 'button' },
            text: 'Next'
        });
        const actions = createElement('div', {
            className: 'app-kb__actions',
            children: [dismissButton, primaryButton]
        });
        const preview = createElement('div', {
            className: 'app-kb__preview',
            children: [previewLabel, previewText]
        });
        const toolbar = createElement('div', {
            className: 'app-kb__toolbar',
            children: [preview, actions]
        });
        const errorEl = createElement('p', {
            className: 'app-kb__error',
            attrs: { role: 'alert', hidden: '' }
        });
        const rows = createElement('div', { className: 'app-kb__rows' });
        const tray = createElement('div', {
            className: 'app-kb',
            attrs: { id: KEYBOARD_TRAY_ID },
            children: [toolbar, errorEl, rows]
        });
        tray.hidden = true;

        document.body.appendChild(tray);
        tray.addEventListener('pointerdown', (event) => event.preventDefault());
        tray.addEventListener('click', (event) => event.stopPropagation());
        this.tray = tray;
        this.rowsEl = rows;
        this.previewEl = preview;
        this.errorEl = errorEl;
        this.previewLabelEl = previewLabel;
        this.previewTextEl = previewText;
        this.previewBeforeEl = previewBefore;
        this.previewAfterEl = previewAfter;
        this.dismissButton = dismissButton;
        this.primaryButton = primaryButton;
        this.bindKey(dismissButton, () => this.hide(true), false);
        this.bindKey(primaryButton, () => this.activatePrimaryAction(), false);
        this.bindCaretGestures(previewText);
    }

    attach(el, rawOptions) {
        this.ensureTray();
        const options = { ...($.keyboard.defaults || {}), ...rawOptions };
        el[OPTIONS_KEY] = options;
        if (!el.__appKeyboardBound) {
            el.__appKeyboardBound = true;
            el.classList.add('app-kb-target');
            el.readOnly = true;
            patchConstraintMethods(el);
            bindFormConstraintBridge(el);
            el.addEventListener('focus', () => this.show(el, el[OPTIONS_KEY]));
            el.addEventListener('focusout', () => {
                setTimeout(() => {
                    const activeEl = document.activeElement;
                    if (activeEl === el || (this.tray && this.tray.contains(activeEl))) {
                        return;
                    }
                    if (this.state && this.state.el === el) {
                        this.hide();
                    }
                }, 0);
            });
        }
        if (document.activeElement === el) {
            this.show(el, options);
            return;
        }
        if (this.state) {
            this.updateActionButtons();
        }
    }

    show(el, options) {
        this.ensureTray();
        if (this.state && this.state.el === el && !this.tray.hidden) {
            if (this.state.options === options) {
                return;
            }
            this.state.options = options;
            this.state.shiftActive = false;
            this.state.altActive = false;
            this.state.symActive = false;
            this.recomputeAutoShift();
            this.updateNumericTheme();
            this.updatePreview();
            this.updateActionButtons();
            this.renderKeyset();
            return;
        }
        if (this.state && this.state.el !== el) {
            this.hide();
        }
        this.state = {
            el,
            options,
            openedValue: el.value,
            shiftActive: false,
            altActive: false,
            symActive: false,
            primaryMode: null,
            submitControl: null,
            errorMessage: ''
        };
        this.recomputeAutoShift();
        this.updateNumericTheme();
        this.moveTrayIntoTopLayerOf(el);
        this.tray.hidden = false;
        this.updatePreview();
        this.updateErrorMessage();
        this.updateActionButtons();
        this.renderKeyset();
        if (typeof options.visible === 'function') {
            options.visible(null, this.state, el);
        }
    }

    moveTrayIntoTopLayerOf(el) {
        const host = el.closest('dialog[open]') || document.body;
        if (this.tray.parentElement !== host) {
            host.appendChild(this.tray);
        }
    }

    updateNumericTheme() {
        if (!this.tray || !this.state) {
            return;
        }
        const { options, altActive, symActive } = this.state;
        const useNumeric = Boolean(options.numeric) && !altActive && !symActive;
        this.tray.classList.toggle(NUMERIC_THEME_CLASS, useNumeric);
    }

    updateActionButtons() {
        if (!this.state) {
            return;
        }
        this.dismissButton.textContent = this.state.options.dismissLabel || 'Close';
        if (this.state.options.accept === 'ok') {
            this.state.primaryMode = 'ok';
            this.state.submitControl = null;
            this.primaryButton.textContent = 'OK';
            this.primaryButton.hidden = false;
            this.primaryButton.disabled = false;
            return;
        }
        const { el } = this.state;
        if (el.type === 'search') {
            this.state.primaryMode = 'search';
            this.state.submitControl = null;
            this.primaryButton.textContent = 'Search';
            this.primaryButton.hidden = false;
            this.primaryButton.disabled = false;
            return;
        }
        const namedAccept = findNamedAcceptControl(el);
        if (namedAccept) {
            this.state.primaryMode = 'submit';
            this.state.submitControl = namedAccept;
            this.primaryButton.textContent = getControlLabel(namedAccept) || 'Done';
            this.primaryButton.hidden = false;
            this.primaryButton.disabled = Boolean(namedAccept.disabled);
            return;
        }
        const nextField = findNextKeyboardField(el);
        if (nextField) {
            this.state.primaryMode = 'next';
            this.state.submitControl = null;
            this.primaryButton.textContent = 'Next';
            this.primaryButton.hidden = false;
            this.primaryButton.disabled = false;
            return;
        }
        const submitControl = findFormSubmitControl(el);
        if (submitControl) {
            this.state.primaryMode = 'submit';
            this.state.submitControl = submitControl;
            this.primaryButton.textContent = getControlLabel(submitControl) || 'Done';
            this.primaryButton.hidden = false;
            this.primaryButton.disabled = Boolean(submitControl.disabled);
            return;
        }
        this.state.primaryMode = null;
        this.state.submitControl = null;
        this.primaryButton.hidden = true;
        this.primaryButton.disabled = false;
    }

    activatePrimaryAction() {
        if (!this.state) {
            return;
        }
        if (this.state.primaryMode === 'ok' || this.state.primaryMode === 'search') {
            this.hide();
            return;
        }
        if (this.state.primaryMode === 'next') {
            const nextField = findNextKeyboardField(this.state.el);
            if (!nextField) {
                this.updateActionButtons();
                return;
            }
            nextField.focus();
            return;
        }
        if (this.state.primaryMode === 'submit') {
            const submitControl = this.state.submitControl;
            if (submitControl && submitControl.disabled) {
                return;
            }
            this.hide();
            if (submitControl) {
                submitControl.click();
            }
        }
    }

    hide(restore) {
        if (!this.state) {
            return;
        }
        this.stopActiveRepeat?.();
        const { el, options } = this.state;
        if (restore === true && options.restoreOnDismiss) {
            el.value = this.state.openedValue;
            const caret = el.value.length;
            el.setSelectionRange(caret, caret);
            el.dispatchEvent(new Event('input', { bubbles: true }));
        }
        el.dispatchEvent(new Event('change', { bubbles: true }));
        this.tray.hidden = true;
        this.tray.classList.remove(NUMERIC_THEME_CLASS);
        if (typeof options.beforeClose === 'function') {
            options.beforeClose(null, this.state, el);
        }
        this.state = null;
        this.updateErrorMessage();
        el.blur();
    }

    onFieldInvalid(event) {
        const el = event.target;
        if (!el || !el.__appKeyboardBound) {
            return;
        }
        event.preventDefault();
        const message = el.validationMessage;
        if (!this.state || this.state.el !== el) {
            this.show(el, el[OPTIONS_KEY]);
        }
        this.setErrorMessage(message);
        if (document.activeElement !== el) {
            el.focus({ preventScroll: true });
        }
    }

    setErrorMessage(message) {
        if (!this.state) {
            return;
        }
        this.state.errorMessage = message;
        this.updateErrorMessage();
    }

    updateErrorMessage() {
        if (!this.errorEl) {
            return;
        }
        const message = this.state ? (this.state.errorMessage || '') : '';
        const errorHidden = !message;
        if (this.errorEl.textContent !== message) {
            this.errorEl.textContent = message;
        }
        if (this.errorEl.hidden !== errorHidden) {
            this.errorEl.hidden = errorHidden;
        }
        this.previewEl.classList.toggle('app-kb__preview--invalid', Boolean(message));
    }

    updatePreview() {
        const { el, options } = this.state;
        this.previewLabelEl.textContent = getFieldPreviewLabel(el);
        this.previewTextEl.dir = el.dir === 'rtl' ? 'rtl' : 'ltr';
        const displayValue = options.maskPreview ? '•'.repeat(el.value.length) : el.value;
        const caret = el.selectionStart ?? displayValue.length;
        this.previewBeforeEl.textContent = displayValue.slice(0, caret);
        this.previewAfterEl.textContent = displayValue.slice(caret);
        this.caretMarkerEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    resolveCaretIndexFromPoint(clientX, clientY) {
        let node = null;
        let offset = 0;
        if (document.caretRangeFromPoint) {
            const range = document.caretRangeFromPoint(clientX, clientY);
            if (!range) {
                return null;
            }
            node = range.startContainer;
            offset = range.startOffset;
        } else if (document.caretPositionFromPoint) {
            const position = document.caretPositionFromPoint(clientX, clientY);
            if (!position) {
                return null;
            }
            node = position.offsetNode;
            offset = position.offset;
        } else {
            return null;
        }

        if (node === this.previewBeforeEl || this.previewBeforeEl.contains(node)) {
            return offset;
        }
        if (node === this.previewAfterEl || this.previewAfterEl.contains(node)) {
            return this.previewBeforeEl.textContent.length + offset;
        }
        return null;
    }

    moveCaretToPoint(clientX, clientY) {
        const index = this.resolveCaretIndexFromPoint(clientX, clientY);
        if (index === null || !this.state) {
            return;
        }
        this.state.el.setSelectionRange(index, index);
        this.updatePreview();
    }

    moveCaretBy(steps) {
        if (!this.state) {
            return;
        }
        const { el } = this.state;
        const current = el.selectionStart ?? el.value.length;
        const next = Math.max(0, Math.min(el.value.length, current + steps));
        el.setSelectionRange(next, next);
        this.updatePreview();
    }

    bindSpaceKey(button) {
        const stepPx = 18;
        let dragging = false;
        let dragged = false;
        let lastX = 0;
        let accumulatedX = 0;

        const onPointerMove = (event) => {
            if (!dragging) {
                return;
            }
            event.preventDefault();
            accumulatedX += event.clientX - lastX;
            lastX = event.clientX;
            const steps = Math.trunc(accumulatedX / stepPx);
            if (steps !== 0) {
                accumulatedX -= steps * stepPx;
                dragged = true;
                this.tray.classList.add('app-kb--space-drag');
                this.moveCaretBy(steps);
            }
        };

        const stopDragging = () => {
            dragging = false;
            button.classList.remove('app-kb__key--touched');
            this.tray.classList.remove('app-kb--space-drag');
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', stopDragging);
            document.removeEventListener('pointercancel', stopDragging);
        };

        button.addEventListener('pointerdown', (event) => {
            event.preventDefault();
            button.classList.add('app-kb__key--touched');
            dragging = true;
            dragged = false;
            lastX = event.clientX;
            accumulatedX = 0;
            document.addEventListener('pointermove', onPointerMove);
            document.addEventListener('pointerup', stopDragging);
            document.addEventListener('pointercancel', stopDragging);
        });

        button.addEventListener('click', (event) => {
            event.preventDefault();
            if (!dragged) {
                this.insertText(' ');
                this.fireCallbacks(event, true);
            }
        });
    }

    bindCaretGestures(surface) {
        const longPressDelayMs = 500;
        const moveThresholdPx = 8;
        let pressTimer = null;
        let dragging = false;
        let movedPastThreshold = false;
        let startX = 0;
        let startY = 0;

        const cancelPress = () => {
            clearTimeout(pressTimer);
            pressTimer = null;
        };

        const onPointerMove = (event) => {
            if (!dragging) {
                return;
            }
            event.preventDefault();
            this.moveCaretToPoint(event.clientX, event.clientY);
        };

        const stopDragging = () => {
            dragging = false;
            this.previewTextEl.classList.remove('app-kb--caret-dragging');
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', stopDragging);
            document.removeEventListener('pointercancel', stopDragging);
        };

        const startDragging = (clientX, clientY) => {
            this.moveCaretToPoint(clientX, clientY);
            dragging = true;
            this.previewTextEl.classList.add('app-kb--caret-dragging');
            document.addEventListener('pointermove', onPointerMove);
            document.addEventListener('pointerup', stopDragging);
            document.addEventListener('pointercancel', stopDragging);
        };

        surface.addEventListener('pointerdown', (event) => {
            startX = event.clientX;
            startY = event.clientY;
            movedPastThreshold = false;
            pressTimer = setTimeout(() => {
                pressTimer = null;
                startDragging(event.clientX, event.clientY);
            }, longPressDelayMs);
        });

        surface.addEventListener('pointermove', (event) => {
            if (!pressTimer) {
                return;
            }
            if (Math.abs(event.clientX - startX) > moveThresholdPx || Math.abs(event.clientY - startY) > moveThresholdPx) {
                movedPastThreshold = true;
                cancelPress();
            }
        });

        surface.addEventListener('pointerup', (event) => {
            const wasAwaitingLongPress = pressTimer !== null;
            cancelPress();
            if (wasAwaitingLongPress && !movedPastThreshold) {
                this.moveCaretToPoint(event.clientX, event.clientY);
            }
        });
        surface.addEventListener('pointerleave', cancelPress);
        surface.addEventListener('pointercancel', cancelPress);
    }

    currentRows() {
        const { options, altActive, shiftActive, symActive } = this.state;
        const layout = options.customLayout;
        if (symActive && layout.symbols) {
            return layout.symbols;
        }
        if (altActive && layout.alt) {
            return layout.alt;
        }
        if (shiftActive && layout.shift) {
            return layout.shift;
        }
        return layout.normal;
    }

    renderKeyset() {
        const rows = this.currentRows();
        this.rowsEl.replaceChildren();
        rows.forEach((rowString) => {
            const rowEl = document.createElement('div');
            rowEl.className = 'app-kb__row';
            rowString.split(/\s+/).filter(Boolean).forEach((token) => {
                rowEl.appendChild(this.buildKey(token));
            });
            this.rowsEl.appendChild(rowEl);
        });
    }

    buildKey(token) {
        if (token.startsWith('{sp:')) {
            const spacer = document.createElement('span');
            spacer.className = 'app-kb__spacer';
            return spacer;
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'app-kb__key';

        const { options, shiftActive } = this.state;

        switch (token) {
            case '{b}':
                button.classList.add('app-kb__key--func', 'app-kb__key--backspace');
                button.appendChild(createIcon('fa-solid fa-delete-left'));
                button.setAttribute('aria-label', 'Backspace');
                this.bindRepeatingKey(button, () => this.backspace());
                return button;
            case '{shift}':
                button.classList.add('app-kb__key--func');
                if (shiftActive) {
                    button.classList.add('app-kb__key--active');
                }
                button.appendChild(createIcon('fa-solid fa-up-long'));
                button.setAttribute('aria-label', 'Shift');
                this.bindKey(button, () => this.toggleShift(), false);
                return button;
            case '{alt}':
                button.classList.add('app-kb__key--func');
                button.textContent = (options.display && options.display.alt) || 'ALT';
                this.bindKey(button, () => this.setAlt(true), false);
                return button;
            case '{normal}':
                button.classList.add('app-kb__key--func');
                button.textContent = (options.display && options.display.normal) || 'ABC';
                this.bindKey(button, () => this.setAlt(false), false);
                return button;
            case '{sym}':
                button.classList.add('app-kb__key--func');
                button.textContent = '#+=';
                this.bindKey(button, () => this.setSym(true), false);
                return button;
            case '{abc}':
                button.classList.add('app-kb__key--func');
                button.textContent = 'ABC';
                this.bindKey(button, () => this.setSym(false), false);
                return button;
            case '{enter}':
                button.classList.add('app-kb__key--func');
                button.appendChild(createIcon('fa-solid fa-arrow-turn-down fa-rotate-90'));
                button.setAttribute('aria-label', 'New line');
                this.bindKey(button, () => this.insertText('\n'));
                return button;
            case '{space}':
                button.classList.add('app-kb__key--space');
                button.textContent = 'Space';
                this.bindSpaceKey(button);
                return button;
            default:
                button.textContent = token;
                this.bindKey(button, () => this.insertText(token));
                return button;
        }
    }

    fireCallbacks(event, isValueKey) {
        if (!this.state) {
            return;
        }
        if (typeof this.state.options.keyboardChange === 'function') {
            this.state.options.keyboardChange(event, this.state, this.state.el);
        }
        if (isValueKey && typeof this.state.options.change === 'function') {
            this.state.options.change(event, this.state, this.state.el);
        }
    }

    bindKey(button, action, isValueKey = true) {
        const touchFlashMs = 60;
        let flashTimer = null;
        const release = () => {
            clearTimeout(flashTimer);
            flashTimer = null;
            button.classList.remove('app-kb__key--touched');
        };
        button.addEventListener('pointerdown', (event) => {
            event.preventDefault();
            clearTimeout(flashTimer);
            button.classList.add('app-kb__key--touched');
            flashTimer = setTimeout(release, touchFlashMs);
        });
        button.addEventListener('pointerup', release);
        button.addEventListener('pointerleave', release);
        button.addEventListener('pointercancel', release);
        button.addEventListener('click', (event) => {
            event.preventDefault();
            action();
            this.fireCallbacks(event, isValueKey);
        });
    }

    bindRepeatingKey(button, action) {
        const repeatDelayMs = 450;
        const repeatIntervalMs = 80;
        let repeatTimer = null;
        let didRepeat = false;

        const release = () => {
            button.classList.remove('app-kb__key--touched');
            stopRepeat();
        };

        const stopRepeat = () => {
            clearTimeout(repeatTimer);
            clearInterval(repeatTimer);
            repeatTimer = null;
            document.removeEventListener('pointerup', release);
            document.removeEventListener('pointercancel', release);
            if (this.stopActiveRepeat === stopRepeat) {
                this.stopActiveRepeat = null;
            }
        };

        button.addEventListener('pointerdown', (event) => {
            event.preventDefault();
            this.stopActiveRepeat?.();
            this.stopActiveRepeat = stopRepeat;
            button.classList.add('app-kb__key--touched');
            didRepeat = false;
            document.addEventListener('pointerup', release);
            document.addEventListener('pointercancel', release);
            repeatTimer = setTimeout(() => {
                didRepeat = true;
                repeatTimer = setInterval(() => {
                    action();
                    this.fireCallbacks(event, true);
                }, repeatIntervalMs);
            }, repeatDelayMs);
        });

        button.addEventListener('pointerup', release);
        button.addEventListener('pointerleave', release);
        button.addEventListener('pointercancel', release);

        button.addEventListener('click', (event) => {
            event.preventDefault();
            if (!didRepeat) {
                action();
                this.fireCallbacks(event, true);
            }
            didRepeat = false;
        });
    }

    toggleShift() {
        this.state.shiftActive = !this.state.shiftActive;
        this.renderKeyset();
    }

    setAlt(active) {
        this.state.altActive = active;
        this.state.shiftActive = false;
        this.state.symActive = false;
        this.updateNumericTheme();
        this.renderKeyset();
    }

    setSym(active) {
        this.state.symActive = active;
        this.state.shiftActive = false;
        this.state.altActive = false;
        this.updateNumericTheme();
        this.renderKeyset();
    }

    recomputeAutoShift() {
        const { el, options } = this.state;
        if (!options.autoShift) {
            return;
        }
        const caret = el.selectionStart || 0;
        const recentText = el.value.substring(Math.max(0, caret - 2), caret);
        const atStart = caret === 0;
        const atNewSentence = ['. ', '! ', '? '].some((delimiter) => recentText.includes(delimiter));
        this.state.shiftActive = atStart || atNewSentence;
    }

    afterMutation() {
        const shiftBefore = this.state.shiftActive;
        this.recomputeAutoShift();
        this.updatePreview();
        if (this.state.errorMessage) {
            this.state.errorMessage = '';
            this.updateErrorMessage();
        }
        this.updateActionButtons();
        if (this.state.shiftActive !== shiftBefore) {
            this.renderKeyset();
        }
    }

    insertText(text) {
        const { el, options } = this.state;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        let newValue = el.value.slice(0, start) + text + el.value.slice(end);
        let caret = start + text.length;
        if (options.maxLength && newValue.length > options.maxLength) {
            newValue = newValue.slice(0, options.maxLength);
            caret = Math.min(caret, options.maxLength);
        }
        this.state.lastKey = text;
        el.value = newValue;
        el.setSelectionRange(caret, caret);
        el.dispatchEvent(new Event('input', { bubbles: true }));
        this.afterMutation();
    }

    backspace() {
        const { el } = this.state;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        if (start === end) {
            if (start === 0) {
                return;
            }
            el.value = el.value.slice(0, start - 1) + el.value.slice(end);
            el.setSelectionRange(start - 1, start - 1);
        } else {
            el.value = el.value.slice(0, start) + el.value.slice(end);
            el.setSelectionRange(start, start);
        }
        this.state.lastKey = 'bksp';
        el.dispatchEvent(new Event('input', { bubbles: true }));
        this.afterMutation();
    }
}

export const keyboardEngine = getSharedKeyboardEngine();

function getSharedKeyboardEngine() {
    if (!document[SHARED_ENGINE_KEY]) {
        document[SHARED_ENGINE_KEY] = new KeyboardEngine();
    }
    return document[SHARED_ENGINE_KEY];
}

$.keyboard = { defaults: {} };
$.fn.keyboard = function attachKeyboardPlugin(options) {
    this.each(function attachOne() {
        keyboardEngine.attach(this, options);
    });
    return this;
};
